import { useMemo } from 'react';
import { format, startOfDay, endOfDay, addDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, parseISO, parse } from 'date-fns';
import AppointmentCard from './AppointmentCard';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface AppointmentCalendarViewProps {
  appointments: any[];
  viewMode: 'day' | 'tomorrow' | 'week';
  selectedDate: Date;
  onViewAppointment?: (appointment: any) => void;
  onCompleteAppointment?: (appointment: any) => void;
  onRescheduleAppointment?: (appointment: any) => void;
}

export default function AppointmentCalendarView({
  appointments,
  viewMode,
  selectedDate,
  onViewAppointment,
  onCompleteAppointment,
  onRescheduleAppointment,
}: AppointmentCalendarViewProps) {
  const displayDates = useMemo(() => {
    if (viewMode === 'day') {
      return [selectedDate];
    } else if (viewMode === 'tomorrow') {
      return [addDays(selectedDate, 1)];
    } else {
      const start = startOfWeek(selectedDate, { weekStartsOn: 1 }); // Monday
      const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    }
  }, [viewMode, selectedDate]);

  const getAppointmentsForDate = (date: Date) => {
    return appointments.filter((apt) => {
      if (!apt.preferred_date) return false;
      try {
        const aptDate = parseISO(apt.preferred_date);
        return isSameDay(aptDate, date);
      } catch {
        return false;
      }
    });
  };

  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 8; hour <= 18; hour++) {
      slots.push({
        hour,
        label: format(new Date().setHours(hour, 0, 0, 0), 'h:mm a'),
      });
    }
    return slots;
  }, []);

  // Helper function to calculate appointment position in grid
  const getAppointmentPosition = (appointment: any) => {
    if (!appointment.scheduled_start_time) return null;
    
    try {
      const startTime = new Date(appointment.scheduled_start_time);
      const endTime = appointment.scheduled_end_time ? new Date(appointment.scheduled_end_time) : new Date(startTime.getTime() + 60 * 60000);
      
      const startHour = startTime.getHours();
      const startMinute = startTime.getMinutes();
      const duration = (endTime.getTime() - startTime.getTime()) / (1000 * 60); // Duration in minutes
      
      // Calculate position from 8 AM
      const topPosition = ((startHour - 8) * 60 + startMinute) / 60; // Hours from 8 AM
      const height = duration / 60; // Height in hours
      
      return { top: topPosition, height };
    } catch {
      return null;
    }
  };

  if (viewMode === 'week') {
    return (
      <div className="grid grid-cols-7 gap-3">
        {displayDates.map((date) => {
          const dayAppointments = getAppointmentsForDate(date);
          const isToday = isSameDay(date, new Date());

          return (
            <div key={date.toISOString()} className="space-y-2">
              <Card
                className={`p-3 text-center ${
                  isToday
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted/50'
                }`}
              >
                <div className="text-xs font-medium">{format(date, 'EEE')}</div>
                <div className="text-lg font-bold">{format(date, 'd')}</div>
                <div className="text-xs">{format(date, 'MMM')}</div>
              </Card>

              <div className="space-y-2">
                {dayAppointments.length > 0 ? (
                  dayAppointments.map((apt) => (
                    <AppointmentCard
                      key={apt.id}
                      appointment={apt}
                      onView={onViewAppointment}
                      onComplete={onCompleteAppointment}
                      onReschedule={onRescheduleAppointment}
                    />
                  ))
                ) : (
                  <Card className="p-4 text-center border-dashed">
                    <p className="text-xs text-muted-foreground">No appointments</p>
                  </Card>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Day or Tomorrow view - TIME GRID LAYOUT
  const currentDate = displayDates[0];
  const dayAppointments = getAppointmentsForDate(currentDate);
  const scheduledAppointments = dayAppointments.filter(apt => apt.scheduled_start_time);
  const unscheduledAppointments = dayAppointments.filter(apt => !apt.scheduled_start_time);

  return (
    <div className="space-y-4">
      <Card className="p-4 bg-gradient-to-r from-primary/10 to-secondary/10 border-2">
        <h3 className="text-2xl font-bold">
          {viewMode === 'tomorrow' ? 'Tomorrow - ' : ''}
          {format(currentDate, 'EEEE, MMMM d, yyyy')}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {scheduledAppointments.length} scheduled · {unscheduledAppointments.length} unscheduled
        </p>
      </Card>

      {/* TIME GRID VIEW */}
      {scheduledAppointments.length > 0 ? (
        <Card className="p-4">
          <h4 className="text-sm font-semibold mb-3">Scheduled Appointments</h4>
          <div className="relative">
            {/* Time slots grid */}
            <div className="space-y-0 border-l-2 border-border">
              {timeSlots.map((slot) => (
                <div
                  key={slot.hour}
                  className="relative h-20 border-b border-border"
                >
                  <div className="absolute -left-16 top-0 w-14 text-xs text-muted-foreground font-medium">
                    {slot.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Appointments positioned absolutely */}
            <div className="absolute inset-0 left-4 pointer-events-none">
              {scheduledAppointments.map((apt) => {
                const position = getAppointmentPosition(apt);
                if (!position) return null;

                const isAfterHours = apt.requires_approval;
                const isConfirmed = apt.status === 'confirmed';
                const isPending = apt.status === 'pending' || apt.status === 'pending_approval';

                return (
                  <div
                    key={apt.id}
                    className="absolute left-0 right-4 pointer-events-auto"
                    style={{
                      top: `${position.top * 5}rem`, // 5rem = 80px (h-20)
                      height: `${position.height * 5}rem`,
                      minHeight: '3rem',
                    }}
                  >
                    <Card
                      className={cn(
                        "h-full p-3 cursor-pointer hover:shadow-lg transition-all border-2",
                        isAfterHours && "border-orange-400 bg-orange-50",
                        !isAfterHours && isConfirmed && "border-green-400 bg-green-50",
                        isPending && "border-yellow-400 bg-yellow-50"
                      )}
                      onClick={() => onViewAppointment?.(apt)}
                    >
                      <div className="flex flex-col h-full justify-between text-xs">
                        <div>
                          <p className="font-bold truncate">{apt.name}</p>
                          <p className="text-[10px] text-muted-foreground truncate">
                            {format(new Date(apt.scheduled_start_time), 'h:mm a')} - 
                            {apt.scheduled_end_time && format(new Date(apt.scheduled_end_time), 'h:mm a')}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {isAfterHours && (
                            <Badge variant="secondary" className="text-[9px] px-1 py-0">
                              After Hours
                            </Badge>
                          )}
                          <Badge 
                            variant={isConfirmed ? "default" : "outline"}
                            className="text-[9px] px-1 py-0"
                          >
                            {apt.status}
                          </Badge>
                        </div>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      ) : (
        <Card className="p-12 text-center border-dashed">
          <div className="text-muted-foreground">
            <p className="text-lg font-semibold mb-2">No scheduled appointments</p>
            <p className="text-sm">
              {viewMode === 'tomorrow'
                ? 'No scheduled appointments for tomorrow.'
                : 'No scheduled appointments for this day.'}
            </p>
          </div>
        </Card>
      )}

      {/* UNSCHEDULED APPOINTMENTS SECTION */}
      {unscheduledAppointments.length > 0 && (
        <Card className="p-4 bg-amber-50 border-amber-200">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Badge variant="outline" className="bg-amber-100">
              {unscheduledAppointments.length}
            </Badge>
            Unscheduled Appointments (Needs Time Assignment)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {unscheduledAppointments.map((apt) => (
              <AppointmentCard
                key={apt.id}
                appointment={apt}
                onView={onViewAppointment}
                onComplete={onCompleteAppointment}
                onReschedule={onRescheduleAppointment}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Legend */}
      <Card className="p-3 bg-muted/30">
        <h4 className="text-xs font-semibold mb-2">Legend</h4>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-green-400 bg-green-50"></div>
            <span>Regular Hours</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-orange-400 bg-orange-50"></div>
            <span>After Hours</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-yellow-400 bg-yellow-50"></div>
            <span>Pending</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-amber-200 bg-amber-50"></div>
            <span>Unscheduled</span>
          </div>
        </div>
      </Card>
    </div>
  );
}
